import {
  from,
  split,
  HttpLink,
  ServerError,
  ServerParseError,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { NetworkError } from "@apollo/client/errors";
import { createClient, MessageType } from "graphql-ws";
import { datadogRum } from "@datadog/browser-rum";
import { Kind, OperationTypeNode } from "graphql";

import { mfeName } from "@/utils/constants";
import isSSR from "@/utils/isSSR";
import { graphNames } from "@/utils/http";

import { webSocketStateVar } from "../reactive-vars";

type CreateCompositeLinkOptions = {
  basePath: string;
  subscriptionUrl: string;
  enableSubscriptionLogging?: boolean;
};

function isServerError(
  networkError: NetworkError,
): networkError is ServerError {
  return Boolean(
    networkError && "statusCode" in networkError && "result" in networkError,
  );
}

function isServerParseError(
  networkError: NetworkError,
): networkError is ServerParseError {
  return Boolean(
    networkError && "statusCode" in networkError && "bodyText" in networkError,
  );
}

const createSubscriptionToken = async (basePath = "") => {
  try {
    const response = await fetch(`${basePath}/api/bfg_graph`, {
      method: "POST",
      body: JSON.stringify({
        operationName: "CreateSubscriptionToken",
        query: `mutation CreateSubscriptionToken {
          createSubscriptionToken {
            ... on CreateSubscriptionTokenResponse {
              token
            }
            ... on GraphError {
              message
            }
            __typename
          }
        }`,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Network error: ${response.status} ${response.statusText}`,
      );
    }

    const { data, error } = await response.json();

    if (error) {
      throw new Error(`GraphQL error: ${error.message}`);
    }

    if (data?.createSubscriptionToken?.__typename === "GraphError") {
      throw new Error(
        `GraphQL error: ${data?.createSubscriptionToken?.message}`,
      );
    }

    return data?.createSubscriptionToken?.token;
  } catch (error) {
    datadogRum.addError(error, {
      mfe: mfeName,
    });
    throw error;
  }
};

const createCompositeLink = ({
  basePath,
  subscriptionUrl,
  enableSubscriptionLogging,
}: CreateCompositeLinkOptions) => {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: subscriptionUrl,
      lazyCloseTimeout: 10000,
      connectionParams: async () => {
        const subscriptionToken = await createSubscriptionToken(basePath);
        return {
          authorization: `Subscription ${subscriptionToken}`,
        };
      },
      retryAttempts: 5,
      retryWait: async () => {
        await new Promise((resolve) => {
          let timeUntilRetry = 3;
          const count = () => {
            webSocketStateVar({ status: "retrying", timeUntilRetry });
            timeUntilRetry -= 1;
            if (timeUntilRetry > 0) {
              setTimeout(count, 1000);
              return;
            }
            resolve(undefined);
          };
          count();
        });
      },
      on: {
        opened: () => {
          if (enableSubscriptionLogging) {
            datadogRum.addAction("WebSocket opened", {
              mfe: mfeName,
            });
          }
        },
        closed: (event) => {
          webSocketStateVar({ status: "closed" });
          if (enableSubscriptionLogging && event instanceof CloseEvent) {
            datadogRum.addAction("WebSocket closed", {
              mfe: mfeName,
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            });
          }
        },
        connecting: (isRetry) => {
          webSocketStateVar({ status: "connecting" });
          if (enableSubscriptionLogging) {
            datadogRum.addAction("WebSocket connecting", {
              mfe: mfeName,
              isRetry,
            });
          }
        },
        connected: (_socket, payload, wasRetry) => {
          webSocketStateVar({ status: "connected" });
          if (enableSubscriptionLogging) {
            datadogRum.addAction("WebSocket connected", {
              mfe: mfeName,
              payload,
              wasRetry,
            });
          }
        },
        error: (errorOrEvent) => {
          webSocketStateVar({ status: "closed" });
          if (enableSubscriptionLogging) {
            datadogRum.addAction("WebSocket error", {
              mfe: mfeName,
              errorOrEventData:
                errorOrEvent instanceof Error
                  ? errorOrEvent.message
                  : JSON.stringify(errorOrEvent),
            });
          }
        },
        message: (message) => {
          if (enableSubscriptionLogging && message.type !== MessageType.Ping) {
            datadogRum.addAction(`WebSocket message: ${message.type}`, {
              mfe: mfeName,
              type: message.type,
              id: "id" in message ? message.id : undefined,
              payload: "payload" in message ? message.payload : undefined,
            });
          }
        },
      },
    }),
  );

  const httpLink = new HttpLink({
    uri: (operation) => {
      const ctxGraph: string = operation.getContext().graph;
      const graphPrefix =
        ctxGraph in graphNames
          ? graphNames[ctxGraph as keyof typeof graphNames]
          : graphNames.bfg;

      // MSW running in node (unit tests) requires an absolute URL
      return `${
        process.env.NODE_ENV === "test" ? "http://unittest.com" : ""
      }${basePath}/api/${graphPrefix}_graph`;
    },
  });

  const splitLink = !isSSR()
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        wsLink,
        httpLink,
      )
    : httpLink;

  const additiveLink = from([
    onError(({ networkError, graphQLErrors, operation }) => {
      const definition = getMainDefinition(operation.query);
      if (networkError) {
        let errorMessage = "unknown";
        if (isServerError(networkError)) {
          errorMessage =
            typeof networkError.result === "string"
              ? networkError.result
              : JSON.stringify(networkError.result);
        } else if (isServerParseError(networkError)) {
          errorMessage = networkError.bodyText;
        } else {
          errorMessage = networkError.message;
        }
        datadogRum.addError(networkError, {
          mfe: operation.getContext().mfe || "unknown",
          request: {
            operationType:
              definition.kind === Kind.OPERATION_DEFINITION
                ? definition.operation
                : "fragment",
            operationName: definition.name?.value,
          },
          errorMessage,
        });

        if (
          // @ts-expect-error: TODO figure out how to better type this
          networkError.statusCode === 401 &&
          process.env.NEXT_PUBLIC_AUTH0_LOGOUT
        ) {
          window.location.href = process.env.NEXT_PUBLIC_AUTH0_LOGOUT;
        }
      }

      if (graphQLErrors) {
        graphQLErrors.forEach((graphQLerr) => {
          const { originalError, message } = graphQLerr;
          const errorInstance = new Error(message);
          errorInstance.stack = originalError?.stack || "";
          datadogRum.addError(errorInstance, {
            mfe: operation.getContext().mfe || "unknown",
            request: {
              operationType:
                definition.kind === Kind.OPERATION_DEFINITION
                  ? definition.operation
                  : "fragment",
              operationName: definition.name?.value,
            },
            originalError,
            errorMessage: message,
          });
        });
      }
    }),
    splitLink,
  ]);

  return additiveLink;
};

export default createCompositeLink;
